/**
 * Alertes échéances légales — carte preview + envoi manuel.
 * Affiche : jalons J-90/J-60/J-30/J-7 atteints aujourd'hui + vue upcoming.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BellRing, Send, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

const URGENCE_COLORS = {
  critique: "bg-red-100 text-red-700 border-red-200",
  urgent: "bg-orange-100 text-orange-700 border-orange-200",
  attention: "bg-amber-100 text-amber-700 border-amber-200",
  information: "bg-blue-100 text-blue-700 border-blue-200",
  ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
  passée: "bg-slate-200 text-slate-500 border-slate-300",
};

const URGENCE_LABEL = {
  critique: "CRITIQUE", urgent: "URGENT", attention: "ATTENTION",
  information: "INFO", ok: "OK", passée: "Passée",
};

export default function LegalAlertsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/legal/alerts/preview"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/legal/alerts/send-now");
      if (r.data.sent > 0) toast.success(`Email envoyé à ${r.data.recipients} destinataire(s)`);
      else toast.info(r.data.message || "Aucun envoi effectué");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (loading && !data) return null;
  if (!data) return null;

  if (!data.has_profile) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="legal-alerts-card">
        <CardContent className="p-3 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Renseignez la date de clôture d'exercice dans le Profil pour activer les alertes légales.
        </CardContent>
      </Card>
    );
  }

  const hasNewAlerts = (data.new_count || 0) > 0;

  return (
    <Card className="border-purple-200" data-testid="legal-alerts-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <BellRing className="w-4 h-4 text-purple-600" />
            Alertes échéances légales
            <span className="text-xs text-slate-500 font-normal">
              J-90 · J-60 · J-30 · J-7
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">
              {data.recipients_count} destinataire{data.recipients_count > 1 ? "s" : ""}
              {data.recipients_count === 0 ? " (aucun CFO)" : ""}
            </span>
            <Button size="sm" onClick={sendNow} disabled={sending || !hasNewAlerts || data.recipients_count === 0} data-testid="legal-alerts-send-now">
              {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Envoyer maintenant
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {hasNewAlerts ? (
          <div className="space-y-2">
            {data.recap && (
              <div className="p-3 rounded bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-purple-500" data-testid="legal-alerts-recap">
                <p className="text-[10px] uppercase tracking-wider font-bold text-purple-700 mb-1">✨ Résumé IA — priorités du jour</p>
                <p className="text-sm text-indigo-900 leading-relaxed font-medium">{data.recap}</p>
              </div>
            )}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-red-600 uppercase">Jalons atteints aujourd'hui</p>
              {data.alerts.filter((a) => !a.already_sent).map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded" data-testid={`legal-alert-${a.type}`}>
                  <div>
                    <p className="text-sm font-medium">{a.label}</p>
                    <p className="text-xs text-slate-500">Exercice {a.exercice} · {a.article}</p>
                  </div>
                  <Badge className="bg-red-600 text-white">J-{a.jalon} — {formatDateFR(a.deadline)}</Badge>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Aucun jalon exact déclenché aujourd'hui.</p>
        )}

        <div className="border-t pt-2">
          <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Vue d'ensemble</p>
          <div className="space-y-1">
            {data.upcoming.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${URGENCE_COLORS[d.urgence] || URGENCE_COLORS.ok}`}>
                    {URGENCE_LABEL[d.urgence] || d.urgence}
                  </span>
                  <span className="text-slate-700">{d.label}</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {formatDateFR(d.deadline)}
                  {d.days_remaining >= 0
                    ? ` · dans ${d.days_remaining}j`
                    : ` · il y a ${Math.abs(d.days_remaining)}j`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
